import { useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  Fade,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Block, WebBackButton } from "../../components/shared";
import { getUserInfo } from "../../services/me";
import { GetUserInfoResponse } from "../../services/me/interface";
import { useTelegram } from "../../hooks";
import { EmailChangeDrawer } from "../../components/pages/personal-data/EmailChangeDrawer";
import { PhoneChangeDrawer } from "../../components/pages/personal-data/PhoneChangeDrawer";

/**
 * Страница «Личные данные».
 *
 * Двухблочная структура:
 *   • Верхняя карточка — read-only поля (имя и Telegram-юзернейм).
 *     Имя приходит от Telegram при первом логине, менять его смысла нет
 *     (перечитаем из initData при следующем входе). Telegram-хэндл
 *     аналогично.
 *   • Нижняя карточка — редактируемые контакты (email и телефон), каждый
 *     как кликабельная строка со стрелкой. По клику открывается Drawer
 *     со своим флоу подтверждения:
 *       - Email → OTP-код на новый адрес;
 *       - Телефон → одноэкранная форма с PIN'ом.
 *
 * Общей кнопки «Сохранить» больше нет — каждое поле сохраняется
 * независимо через свой drawer.
 */
export function PersonalDataForm() {
  const navigate = useNavigate();
  const { t } = useTranslation(["profile"]);

  const { tg } = useTelegram();
  useEffect(() => {
    const handleBack = () => navigate(-1);
    tg.BackButton.show();
    tg.onEvent("backButtonClicked", handleBack);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handleBack);
    };
  }, [navigate, tg]);

  const { data: me, isLoading } = useQuery<GetUserInfoResponse>(
    ["user-info"],
    getUserInfo,
    { staleTime: 30_000 },
  );

  const [emailOpen, setEmailOpen] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);

  if (isLoading || !me) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  const fullName = [me.first_name, me.last_name]
    .map((s) => (s ?? "").trim())
    .filter(Boolean)
    .join(" ");

  return (
    <Fade in>
      <Box sx={{ width: "100%", marginBottom: "100px" }}>
        <WebBackButton title={t("profile:personal_data.title")} />

        {/* Read-only блок */}
        <Block sx={{ padding: 0, overflow: "hidden" }}>
          <ReadRow
            label={t("profile:personal_data.username", {
              defaultValue: "Имя пользователя",
            })}
            value={fullName || "—"}
          />
          <Divider sx={{ mx: 2, opacity: 0.5 }} />
          <ReadRow
            label={t("profile:personal_data.telegram", {
              defaultValue: "Telegram",
            })}
            value={me.username ? `@${me.username}` : "—"}
          />
        </Block>

        {/* Редактируемые контакты */}
        <Block sx={{ padding: 0, marginTop: "16px", overflow: "hidden" }}>
          <ClickableRow
            label={t("profile:personal_data.email", { defaultValue: "E-mail" })}
            value={me.email ?? "—"}
            onClick={() => setEmailOpen(true)}
          />
          <Divider sx={{ mx: 2, opacity: 0.5 }} />
          <ClickableRow
            label={t("profile:personal_data.phone", {
              defaultValue: "Телефон",
            })}
            value={me.phone_number ?? "—"}
            onClick={() => setPhoneOpen(true)}
          />
        </Block>

        <EmailChangeDrawer
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          currentEmail={me.email}
        />
        <PhoneChangeDrawer
          open={phoneOpen}
          onClose={() => setPhoneOpen(false)}
          currentPhone={me.phone_number}
        />
      </Box>
    </Fade>
  );
}

// ---------- Row primitives ----------

function ReadRow({ label, value }: { label: string; value: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "16px 20px",
        minHeight: 56,
      }}
    >
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 500,
          color: "text.primary",
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "15px",
          color: "text.secondary",
          textAlign: "right",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function ClickableRow({
  label,
  value,
  onClick,
}: {
  label: string;
  value: string;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "16px 20px",
        minHeight: 56,
        width: "100%",
        textAlign: "left",
        transition: "background 0.12s",
        "&:hover": {
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.03)"
              : "rgba(0,0,0,0.02)",
        },
        "&:active": {
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.04)",
        },
      })}
    >
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: 500,
          color: "text.primary",
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "15px",
            color: "text.secondary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {value}
        </Typography>
        <Box
          component="span"
          sx={(theme) => ({
            display: "inline-flex",
            flexShrink: 0,
            color:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.55)"
                : "rgba(0,0,0,0.45)",
          })}
        >
          <Icon
            icon="solar:alt-arrow-right-linear"
            width={18}
            height={18}
            color="currentColor"
          />
        </Box>
      </Box>
    </ButtonBase>
  );
}
